import { FunctionComponent, useEffect, useState, useCallback } from "react";
import DropdownMenu from "../components/dropdown/DropdownMenu";
import SvgFragment from "./SvgFragment";
import styled from "styled-components";
import DropdownItem from "../components/dropdown/DropdownItem";
import Checkbox from "../components/Checkbox";
import Text from "../components/Text";
import Row from "../components/Row";
import { useConnectionsStore } from "../stores/connections/useConnectionsStore";
import { useConnectionsHandlers } from "../services/actions/connections/useConnectionsHandlers";
import ActionText from "../components/ActionText";
import { useAuthStore } from "../stores/auth/useAuthStore";
import { styles } from "../styles/themes/style";
import { AppRoutes } from "../interfaces/store/appStore";
import { useNavigate } from "react-router-dom";
import { useAppHandlers } from "../services/actions/app/useAppHandlers";

interface IConnectionCustomizeColumnsProps { }


const ConnectionCustomizeColumns: FunctionComponent<IConnectionCustomizeColumnsProps> = () => {

    const { setConnectionsParametersHandler } = useConnectionsHandlers()
    const { store: { connectionsParameters, customFieldDefinitions } } = useConnectionsStore()
    const { store: { workspace } } = useAuthStore()
    const { setCreateCustomFieldModal, hideModal } = useAppHandlers()
    const { columnsSetup } = connectionsParameters
    const navigate = useNavigate()
    
    // Build initial columns including custom fields
    const buildInitialColumns = useCallback(() => {
        const baseColumns = [
            { label: "Headline", isSelected: true, value: "headline" },
            { label: "Job Title", isSelected: true, value: "jobTitle" },
            { label: "Company", isSelected: true, value: "company" },
            { label: "Company headcount", isSelected: false, value: "companySize" },
            { label: "Location", isSelected: true, value: "location" },
            { label: "Personal Email", isSelected: true, value: "enrichedPrivateEmails" },
            { label: "Business Email", isSelected: true, value: "enrichedBusinessEmails" },
            { label: "Phone Number", isSelected: true, value: "enrichedPhones" },
            { label: "Date Connected", isSelected: true, value: "dateConnected" },
            { label: "Li Email", isSelected: true, value: "contactInfo" },
            { label: "Li Phone", isSelected: true, value: "liPhone" },
            { label: "Last Messaged", isSelected: !workspace?.isStarter, value: "lastMessaged" },
            { label: "Tags", isSelected: true, value: "tags" },
            { label: "Languages", isSelected: true, value: "languages" },
            { label: "Date Of Birth", isSelected: true, value: "dateOfBirth" },
            { label: "Followers", isSelected: true, value: "followers" },
            { label: "Connected members", isSelected: !workspace?.isStarter, value: "mutualConnections" },
            { label: "Warm Intro AI", isSelected: true, value: "linkedinMutualProfiles" }
        ];

        // Add custom fields from GET /profiles/custom-field-definitions (excluding select and multiselect types)
        const customFieldColumns = (customFieldDefinitions || [])
            // TODO: remove this filter in V2    
            .filter((field: any) => 
                field.type !== 'select' && 
                field.type !== 'multiselect' && 
                field.type !== 'boolean' &&
                field.type !== 'tag'
            )
            .map((field: any) => ({
                label: field.label,
                isSelected: false, // Initially unchecked
                value: `customField_${field.key}`,
                isCustomField: true
            }));

        return [...baseColumns, ...customFieldColumns];
    }, [workspace?.isStarter, customFieldDefinitions]);
    
    const [columns, setColumns] = useState<any>(() => buildInitialColumns())

    const onClickHandler = (props: any) => {
        const { isSelected, value} = props

        // Don't allow toggling createNewField column
        if (value === 'createNewField') return;

        const updated = columns.map((column: any) => {
            if (column.value === value) {
                return {
                    ...column,
                    isSelected: !isSelected
                }
            }
            return column
        })

        setColumns(updated)

        columnsSetup[value] = !isSelected

        localStorage.setItem('columns-setup', JSON.stringify(columnsSetup))

        setConnectionsParametersHandler({...connectionsParameters, columnsSetup: columnsSetup})
    }

    const resetColumns = () => {
        const defaultColumnsSetup: any = {
            "userInfo": true,
            "lastMessaged": !workspace?.isStarter,
            "followers": true,
            "headline": true,
            "jobTitle": true,
            "company": true,
            "companySize": false,
            "location": true,
            "dateConnected": true,
            "contactInfo": true,
            "tags": true,
            "languages": true,
            "dateOfBirth": true,
            "mutualConnections": !workspace?.isStarter,
            "linkedinMutualProfiles": true,
            "enrichedPrivateEmails": true,
            "enrichedBusinessEmails": true,
            "enrichedPhones": true,
            "liPhone": true,
            "createNewField": true
        };

        // Set all custom fields to false by default (excluding select and multiselect types)
        (customFieldDefinitions || [])
            // TODO: remove this filter in V2
            .filter((field: any) => 
                field.type !== 'select' && 
                field.type !== 'multiselect' && 
                field.type !== 'boolean' &&
                field.type !== 'tag'
            )
            .forEach((field: any) => {
                defaultColumnsSetup[`customField_${field.key}`] = false;
            });

        const defaultOrder = [
            "userInfo",
            "headline",
            "jobTitle",
            "company",
            "companySize",
            "location",
            "enrichedPrivateEmails",
            "enrichedBusinessEmails",
            "enrichedPhones",
            "dateConnected",
            "contactInfo",
            "liPhone",
            "lastMessaged",
            "tags",
            "languages",
            "dateOfBirth",
            "followers",
            "mutualConnections",
            "linkedinMutualProfiles",
        ];
        localStorage.setItem('columns-setup', JSON.stringify(defaultColumnsSetup))
        localStorage.setItem('columns-order', JSON.stringify(defaultOrder));
        setConnectionsParametersHandler({
            ...connectionsParameters,
            columnsSetup: defaultColumnsSetup,
            columnsOrder: defaultOrder
        })
    }


    // Update columns when columnsSetup or custom fields change
    useEffect(() => {
        const initialCols = buildInitialColumns();
        const updated = initialCols.map((column: any) => {
            if (columnsSetup && columnsSetup?.hasOwnProperty(column.value)) {
                return {
                    ...column,
                    isSelected: columnsSetup[column.value]
                }
            }
            return column
        })
        setColumns(updated)
    }, [columnsSetup, buildInitialColumns])

    const handleCreateCustomField = () => {
        setCreateCustomFieldModal(() => {
            hideModal();
        });
    };

    return (
        <DropdownMenu autoClose='outside' $hideToggle title={<SvgFragmentWrapper><SvgFragment type='columnsTable' /></SvgFragmentWrapper>} tooltipMessage={'Change columns'}>
            <DropdownContent>
                <FixedHeader>
                    <StyledRow alignItems gap='5px'>
                        <Text $bold>View Columns </Text>
                        (<ActionText onClickHandler={resetColumns}>Reset To Default</ActionText>)
                    </StyledRow>
                </FixedHeader>
                
                <ScrollableColumns>
                    {columns.filter((column: any) => column.value !== 'createNewField').map((column: any) => {
                        return (
                            <DropdownItem $disabled={workspace?.isStarter && (column.value === 'mutualConnections' || column.value === 'lastMessaged')} key={column?.value} onClickHandler={() => onClickHandler(column)}>
                                <Row gap="10px" alignItems>
                                    <StyledCheckbox 
                                        disabled={workspace?.isStarter && (column.value === 'mutualConnections' || column.value === 'lastMessaged')} 
                                        checkboxId={column.value} 
                                        checked={column.isSelected} 
                                        onClickHandler={() => onClickHandler(column)} />
                                    <StyledText >{column.label}</StyledText>
                                    {workspace.isStarter && (column.value === 'mutualConnections' || column.value === 'lastMessaged') &&   
                                        <StyledActionText icon='lock2' 
                                                onClickHandler={(e: any) => {
                                                    e.stopPropagation();
                                                    navigate(AppRoutes.BILLING);
                                                }}
                                        >
                                            { 'Upgrade'}
                                        </StyledActionText>
                                    }
                                </Row>
                            </DropdownItem>
                        )
                    })}
                </ScrollableColumns>
                
                <FixedFooter>
                    <Divider />
                    <CreateFieldButtonWrapper
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                            e.stopPropagation();
                            handleCreateCustomField();
                        }}>
                        <CreateFieldIconWrapper>
                            <SvgFragment type='plus' />
                        </CreateFieldIconWrapper>
                        <CreateFieldLabel>Create new field</CreateFieldLabel>
                    </CreateFieldButtonWrapper>
                </FixedFooter>
            </DropdownContent>
        </DropdownMenu>
    )
}

const DropdownContent = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 490px;
    width: 100%;
`

const FixedHeader = styled.div`
    flex-shrink: 0;
    background: white;
    padding-bottom: 8px;
`

const ScrollableColumns = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
`

const FixedFooter = styled.div`
    flex-shrink: 0;
    background: white;
`

const SvgFragmentWrapper = styled.div`
    width: 34px;
    height: 34px;
    display: flex;
    justify-content: center;
    align-items: center;
`
const StyledCheckbox = styled(Checkbox)`
    height: fit-content;
`
const StyledText = styled(Text)`
    font-weight: normal;
`
const StyledRow = styled(Row)`
    padding: 0 20px;
    text-wrap: nowrap;
`
const StyledActionText = styled(ActionText)`
    span {    
        color: ${styles.colors.black300};
        font-size: 12px;
    }

    &:hover{
        path { fill: ${styles.colors.primary400} }
    }

    div {
        margin-right: 4px;
        svg {
            margin-bottom: 4px;
        }
    }
`
const CreateFieldButtonWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    padding: 8px 16px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${styles.colors.black100};
    }
`
const CreateFieldIconWrapper = styled.div`
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        width: 14px;
        height: 14px;
    }
    
    path {
        fill: ${styles.colors.primary600};
    }
`
const CreateFieldLabel = styled(Text)`
    color: ${styles.colors.black600};
    font-weight: 700;
    font-style: Regular;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
`
const Divider = styled.div`
    width: 100%;
    height: 2px;
    background: ${({ theme: { quinaryColor } }) => `${quinaryColor}`};
    margin: 10px 0;
`

export default ConnectionCustomizeColumns